import React, { Component } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import {
  getCatalogueItemsAction, getNextCatalogueItemsAction, resetCatalogueItemsAction,
} from 'actions/portal';
import { getTenantCatalogueCategoryByCategoryPath, getCategoryTypeFromCatalogueCategory } from 'services/utils/brochure';
import {
  CatalogueItemType,
  CatalogueItemOrder,
  Product,
  Service,
} from 'types/ts/catalogue';
import { RootState } from 'types/ts/state';

import { Props, State } from './index.types';
import PortalCatalogueCategoryScreen from './PortalCatalogueCategoryScreen';

class PortalCatalogueCategoryScreenContainer extends Component<Props, State> {
  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const { tenant, match } = nextProps;
    const { category: previousCategory } = prevState;

    const routeCategory = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
    const category = getCategoryTypeFromCatalogueCategory(routeCategory);

    if (previousCategory !== category) {
      return { category };
    }
    return null;
  }

  constructor(props: Props) {
    super(props);

    const { location, tenant, match } = props;
    const { state } = location;

    const routeCategory = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
    const category = getCategoryTypeFromCatalogueCategory(routeCategory);

    this.state = {
      selectedItem: state?.item ? state.item : null,
      showDetails: false,
      category,
    };
  }

  componentDidMount() {
    const { resetCatalogueItems } = this.props;
    resetCatalogueItems();
    this.getCatalogueItems();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      location, match, tenant, catalogueItems, getNextCatalogueItems, resetCatalogueItems,
    } = this.props;
    const { state } = location;
    const { category: previousCategory, selectedItem } = prevState;

    const routeCategory = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
    const category = getCategoryTypeFromCatalogueCategory(routeCategory);

    if (previousCategory !== category) {
      resetCatalogueItems();
      this.getCatalogueItems();
      return;
    }

    const itemId = match?.params?.itemId;
    if (!itemId && !selectedItem && catalogueItems?.items?.length) {
      const item = catalogueItems.items[0];
      this.redirectToCatalogueItem(item);
      return;
    }

    const { item } = state || {};
    if (item && item?.id !== selectedItem?.id) {
      this.setSelectedItem(item);
      return;
    }

    const hasNextPage = catalogueItems?.pagination?.hasNextPage;
    const loadedItem = catalogueItems.items.find(ci => ci.id === itemId);
    const lastItem = catalogueItems.items.slice(catalogueItems.items.length - 1)[0];
    const isLastItem = lastItem && lastItem.id === itemId;
    const isLoading = catalogueItems?.isLoading;

    if ((isLastItem || !loadedItem) && hasNextPage && !isLoading) {
      getNextCatalogueItems();
      return;
    }

    if (loadedItem && selectedItem?.id !== loadedItem.id) {
      this.setSelectedItem(loadedItem);
    }
  }

  setSelectedItem = (item: Product|Service) => {
    this.setState({
      selectedItem: item,
      showDetails: !!item,
    });
  }

  getCatalogueItems = (sortOrder?: CatalogueItemOrder) => {
    const { match, tenant, getCatalogueItems } = this.props;
    const { params: { homeId } } = match;
    const routeCategory = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
    const type = routeCategory.type as CatalogueItemType;
    const categoryType = getCategoryTypeFromCatalogueCategory(routeCategory);

    getCatalogueItems(type, categoryType, homeId, sortOrder);
  }

  handleGetCatalogueItems = () => {
    this.getCatalogueItems();
  }

  handleSelectCatalogueItem = (item: Product|Service) => {
    const { match } = this.props;
    const itemId = match?.params?.itemId;
    if (item?.id !== itemId) {
      this.redirectToCatalogueItem(item);
    } else {
      this.setState({ showDetails: !!item });
    }
  }

  redirectToCatalogueItem = (item?: Product|Service) => {
    if (item) {
      const { history, match, tenant } = this.props;
      const { params: { homeId } } = match;
      const category = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
      history.push(`/portal/${homeId}/catalogue/${category?.path}/${item.id}`, { item });
    }
  }

  handleHideDetails = () => {
    this.setState({ showDetails: false });
  }

  render() {
    const {
      catalogueItems, home, tenant, match, history,
    } = this.props;
    const { selectedItem, showDetails } = this.state;

    const {
      bereavedPortalStyles, cataloguePdfCategoryDisplayPreferences: displayPreferences,
    } = tenant || {};
    const { bereavedPortalLogo } = bereavedPortalStyles || {};

    const category = getTenantCatalogueCategoryByCategoryPath(tenant, match?.params?.category);
    if (!category || !category.visible) {
      const { params: { homeId } } = match;
      history.push(`/portal/${homeId}`);
      return null;
    }

    return (
      <PortalCatalogueCategoryScreen
        category={category}
        catalogueItems={catalogueItems}
        logoUri={bereavedPortalLogo?.uri}
        displayPreferences={displayPreferences}
        home={home}
        selectedItem={selectedItem}
        showDetails={showDetails}
        getCatalogueItems={this.handleGetCatalogueItems}
        onHideDetails={this.handleHideDetails}
        onSelectCatalogueItem={this.handleSelectCatalogueItem}
      />
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  tenant: state?.portalStore?.tenant,
  home: state?.portalStore?.home,
  catalogueItems: state?.portalStore?.catalogueItems,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
  getCatalogueItems: getCatalogueItemsAction,
  getNextCatalogueItems: getNextCatalogueItemsAction,
  resetCatalogueItems: resetCatalogueItemsAction,
}, dispatch);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PortalCatalogueCategoryScreenContainer),
);
